// src/components/HomePage.js
import React, { useState, useEffect } from 'react';
import { firestore, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, Link } from 'react-router-dom';
import { normalizePuzzleUrl } from '../utils/urlUtils';
import { getActiveEventId } from '../utils/activeEvent';
import {
  matchTeamByPasscodeForEvent,
  TEAM_PASSCODE_MAP_NO_TEAM,
} from '../utils/matchTeamByPasscode';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import { getPublicSiteMarketingConfig, joinMarketingUrl } from '../utils/publicSiteConfig';
import SponsorLogos from './SponsorLogos';
import './HomePage.css';

const getEventPuzzleMetaFn = httpsCallable(functions, 'getEventPuzzleMeta');

const HomePage = ({ forcedEventId = '', year = null }) => {
  const [activeEvent, setActiveEvent] = useState(null);
  const [dashboardPuzzles, setDashboardPuzzles] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [marketingHomeUrl, setMarketingHomeUrl] = useState('');
  const [marketingRulesUrl, setMarketingRulesUrl] = useState('https://perrisfreshmeet.com/rules');
  const [marketingMapUrl, setMarketingMapUrl] = useState('https://perrisfreshmeet.com/map');
  const [loading, setLoading] = useState(true);
  const [passcode, setPasscode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchActiveEventAndPuzzles() {
      try {
        let eventData = null;
        if (forcedEventId) {
          const eventSnap = await getDoc(doc(firestore, 'events', forcedEventId));
          if (eventSnap.exists()) {
            eventData = { id: eventSnap.id, ...eventSnap.data() };
          }
        } else {
          const id = await getActiveEventId();
          if (id) {
            const eventSnap = await getDoc(doc(firestore, 'events', id));
            if (eventSnap.exists()) eventData = { id: eventSnap.id, ...eventSnap.data() };
          }
        }
        if (eventData) {
          setActiveEvent(eventData);
          const metaResult = await getEventPuzzleMetaFn({eventId: eventData.id});
          setDashboardPuzzles((metaResult.data.puzzles || []).filter((p) => p.onDashboard));
          const sponsorListRaw = await fetchSponsorsMergedForEvent(eventData.id);
          const sponsorList = sponsorListRaw
            .filter((s) => !s.hidden && (s.type || 'none') !== 'none' && (s.showOnDashboard !== false));
          sponsorList.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
          setSponsors(sponsorList);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching active event:", error);
        setLoading(false);
      }
    }
    fetchActiveEventAndPuzzles();
  }, [forcedEventId]);

  useEffect(() => {
    let cancelled = false;
    getPublicSiteMarketingConfig().then((cfg) => {
      if (cancelled) return;
      const base = cfg.marketingSiteBaseUrl || '';
      setMarketingHomeUrl(base || 'https://perrisfreshmeet.com');
      setMarketingRulesUrl(joinMarketingUrl(base, cfg.marketingPaths?.rules || '/rules'));
      setMarketingMapUrl(joinMarketingUrl(base, cfg.marketingPaths?.map || '/map'));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    if (passcode.trim() === '') return;

    if (!activeEvent) {
      alert('No active event found.');
      return;
    }

    try {
      const { team: matched, status } = await matchTeamByPasscodeForEvent(
        activeEvent.id,
        passcode
      );

      if (matched) {
        localStorage.setItem('teamId', matched.id);
        navigate('/team-dashboard');
        return;
      }
      if (status === TEAM_PASSCODE_MAP_NO_TEAM) {
        alert('Passcode is valid but no team is set up for this number yet.');
        return;
      }
      alert('Invalid passcode. Please try again.');
    } catch (err) {
      console.error('Error validating passcode:', err);
      alert('There was an error. Please try again later.');
    }
  };

  const handleRegister = () => {
    // Navigate to the registration component where users enter their mentor email, name, and division.
    if (year) {
      navigate(`/${year}/info`);
      return;
    }
    navigate(activeEvent ? `/register?event=${activeEvent.id}` : '/register');
  };

  const goToLeaderboard = () => {
    navigate(activeEvent ? `/leaderboard?event=${activeEvent.id}` : '/leaderboard');
  };

  if (loading) return <p>Loading...</p>;

  const dashboardEnabled = !!activeEvent;

  return (
    <div className="home-page">
      <header className="home-header">
        <img src="/fresh-meet-logo.png" alt="Fresh Meet Logo" className="logo" />
        {activeEvent ? <h1>{activeEvent.title}</h1> : <h1>No Active Event</h1>}
      </header>

      <section className="passcode-section">
        <form onSubmit={handlePasscodeSubmit}>
          <input 
            type="text" 
            placeholder="Enter Team Passcode" 
            value={passcode} 
            onChange={(e) => setPasscode(e.target.value)}
            disabled={!dashboardEnabled}
          />
          <button type="submit" className="btn-fm-primary" disabled={!dashboardEnabled}>Go to Team Dashboard</button>
        </form>
      </section>

      {activeEvent && (
        <nav className="dashboard-menu" aria-label="Main navigation">
          {activeEvent.acceptingTeamNames && (
            <button type="button" onClick={handleRegister} className="dashboard-menu-item dashboard-menu-item-register">
              Register
            </button>
          )}
          {activeEvent.showLeaderboardOnHome && (
            <button type="button" onClick={goToLeaderboard} className="dashboard-menu-item">
              Leaderboard
            </button>
          )}
          <a
            href={year ? `/${year}/schedule` : (activeEvent ? `/schedule?event=${activeEvent.id}` : '/schedule')}
            target="_blank"
            rel="noopener noreferrer"
            className="dashboard-menu-item"
          >
            Schedule
          </a>
          {activeEvent.showMorningLoadOnHome !== false && (
            <Link to={activeEvent ? `/morning-load-order?event=${activeEvent.id}` : '/morning-load-order'} className="dashboard-menu-item">
              Morning load order
            </Link>
          )}
          <a href={marketingRulesUrl} target="_blank" rel="noopener noreferrer" className="dashboard-menu-item">
            Rules
          </a>
          <a href={marketingMapUrl} target="_blank" rel="noopener noreferrer" className="dashboard-menu-item">
            Map
          </a>
          <a href={marketingHomeUrl || '/'} target="_blank" rel="noopener noreferrer" className="dashboard-menu-item">
            Main Site
          </a>
          <Link to={activeEvent ? `/what-can-i-win?event=${activeEvent.id}` : '/what-can-i-win'} className="dashboard-menu-item">
            What can I win?
          </Link>
        </nav>
      )}

      {activeEvent && activeEvent.acceptingTeamNames && (
        <p className="dashboard-menu-hint">Enter your mentor email, your name, and choose a division to register your team.</p>
      )}
      
      {activeEvent?.showBonusPuzzlesOnHome && dashboardPuzzles.length > 0 && (
        <section className="announcements-section bonus-puzzles-section">
          <h2>Available bonus puzzles</h2>
          <ul className="dashboard-puzzle-list">
            {dashboardPuzzles.map((puzzle) => {
              const rawUrl = (puzzle.dashboardUrl || '').trim() || (puzzle.publicCode ? `/p/${puzzle.publicCode}` : null);
              const interactUrl = rawUrl ? normalizePuzzleUrl(rawUrl) : null;
              return (
                <li key={puzzle.id} className="dashboard-puzzle-item">
                  <span className="dashboard-puzzle-title">
                    #{puzzle.number} — {puzzle.title || 'Bonus clue'}
                  </span>
                  {interactUrl ? (
                    <a
                      href={interactUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dashboard-puzzle-link"
                    >
                      Open puzzle
                    </a>
                  ) : (
                    <span className="dashboard-puzzle-link disabled">No URL set</span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {activeEvent && sponsors.length > 0 && (
        <SponsorLogos sponsors={sponsors} variant="subtle" />
      )}
    </div>
  );
};

export default HomePage;
